import { FC } from 'react'

import styled from 'styled-components'

import {
  AudioFileUpload,
  AudioFileUploadFileInfo,
  AudioFileUploadStatus,
} from 'src/Components/AudioFileUpload'
import { CTAButton } from 'src/Components/Buttons/CTAButton'
import { PrimaryDottedButton } from 'src/Components/Buttons/PrimaryDottedButton'
import { MAX_MOBILE_WIDTH, variables } from 'src/Styled/variables'

const Container = styled.div`
  display: flex;
  width: 100%;
  height: 100vh;
  padding: 24px 16px 96px 16px;
  flex-direction: column;
  gap: 24px;
  align-items: center;
  @media screen and (min-width: ${MAX_MOBILE_WIDTH}) {
    margin: 0 auto;
    width: 600px;
    padding: 64px 0px 96px 0px;
  }
`

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`

const Title = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: ${variables.fontSize20};
  font-style: normal;
  font-weight: ${variables.fontWeight600};
  line-height: ${variables.lineHeight150per};
`

const SubTitle = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: ${variables.fontSize14};
  font-style: normal;
  font-weight: ${variables.fontWeight400};
  line-height: ${variables.lineHeight150per};
`

const ButtonsContainer = styled.div`
  position: fixed;
  bottom: 0px;
  left: 0px;
  border-top: 1px solid ${({ theme }) => theme.color.lightGrey};
  background: ${({ theme }) => theme.color.white};
  display: flex;
  width: 100%;
  padding: 8px 16px;
  justify-content: center;
  align-items: center;
  gap: 24px;
`

const StyledCTAButton = styled(CTAButton)`
  padding-left: 40px;
  padding-right: 40px;
`

export interface TrackFileModalContentProps {
  file: AudioFileUploadFileInfo | null
  status: AudioFileUploadStatus
  progress: number
  error: string | null
  isSaving: boolean
  canSave: boolean
  onFileSelect: (file: File) => void
  onRemove: () => void
  onSave: () => void
  onClose: () => void
}

export const TrackFileModalContent: FC<TrackFileModalContentProps> = ({
  file,
  status,
  progress,
  error,
  isSaving,
  canSave,
  onFileSelect,
  onRemove,
  onSave,
  onClose,
}) => (
  <Container>
    <TextContainer>
      <Title>Upload your track file</Title>
      <SubTitle>
        Your track is confidential. Only verified curators who agreed not to
        download or share unreleased music will have access.
      </SubTitle>
    </TextContainer>
    <AudioFileUpload
      file={file}
      status={status}
      progress={progress}
      error={error}
      disabled={status === 'uploading'}
      onFileSelect={onFileSelect}
      onRemove={onRemove}
    />
    <ButtonsContainer>
      <PrimaryDottedButton onClick={onClose}>Go Back</PrimaryDottedButton>
      <StyledCTAButton onClick={onSave} disabled={!canSave} loading={isSaving}>
        Save & Continue
      </StyledCTAButton>
    </ButtonsContainer>
  </Container>
)
